/* eslint-disable complexity */
import React, { PropsWithChildren, Children } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { isLocalizableHref } from "@/libraries/web-framework";

import { DataEventProps } from "../../../../libraries/wpl/foundations/src/index";

//Types
export type LinkType =
    | "basic"
    | "basic-underline"
    | "external"
    | "external-icon"
    | "video"
    | "modal"
    | "anchor"
    | "inline"
    | "inline-basic";

export type BgColors = "bgWhite" | "bgLight" | "bgDark";

interface LinkColors {
    bgWhite: Colors;
    bgLight: Colors;
    bgDark: Colors;
}

interface Colors {
    default: string;
    hover: string;
    visited: string;
}

export interface LinkProps {
    text: string;
    href: string;
    type?: LinkType;
    customLinkColor?: string;
    bgColor?: BgColors;
    onClick?: (e: React.MouseEvent) => void;
    className?: string;
    doesArrowAnimate?: boolean;
    ariaLabel?: string;
    dataEventProps?: DataEventProps;
    isStyleOnly?: boolean;
}

//CSS
const linkColors: LinkColors = {
    bgWhite: {
        default: "#0065FF",
        hover: "#0747A6",
        visited: "#403294"
    },
    bgLight: {
        default: "#172B4D",
        hover: "#0747A6",
        visited: "#403294"
    },
    bgDark: {
        default: "#FAFBFC",
        hover: "#4C9AFF",
        visited: "#C0B6F2"
    }
};

//Functions
const Icon = (type: LinkType) => {
    if (
        type === "basic" ||
        type === "basic-underline" ||
        type === "inline-basic"
    ) {
        return (
            <svg
                css={css({
                    margin: "5px 0px 1px 5px",
                    transition: "transform 0.25s ease-in-out"
                })}
                width="11"
                height="8"
                viewBox="0 0 11 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.40796 0L6.32831 1.2021L8.07854 3.1497H0.763182C0.341906 3.1497 0 3.53038 0 3.99943C0 4.46849 0.341906 4.84917 0.763182 4.84917H8.07854L6.32831 6.7979L7.40796 8L11 3.99943L7.40796 0Z"
                />
            </svg>
        );
    }
    if (type === "external" || type === "external-icon") {
        return (
            <svg
                css={css({
                    margin: "5px 0px 2px 5px"
                })}
                width="7"
                height="8"
                viewBox="0 0 7 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2.04815 0.5V1.98788H4.46059L0.217983 6.22935C-0.0726609 6.51998 -0.0726609 6.99114 0.217983 7.28178C0.363801 7.4266 0.553265 7.5 0.744713 7.5C0.935169 7.5 1.12463 7.4266 1.27045 7.28178L5.51206 3.04031V5.45168H7V0.5H2.04815Z"
                />
            </svg>
        );
    }
    if (type === "video") {
        return (
            <svg
                css={css({
                    marginInlineEnd: "3px"
                })}
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14ZM10.3561 7.10299L4.79449 4.21918V9.78082L10.3561 7.10299Z"
                />
            </svg>
        );
    }
    return;
};

const formatLinkText = (text: string, type: LinkType | undefined) => {
    const typeOfLink = type ? type : "basic";
    if (type === "video") {
        return (
            <React.Fragment>
                {Icon(typeOfLink)}
                <span>{text}</span>
            </React.Fragment>
        );
    }
    if (type === "external-icon") {
        return (
            <React.Fragment>
                <span>
                    {text}
                    {Icon(typeOfLink)}
                </span>
            </React.Fragment>
        );
    }
    return (
        <React.Fragment>
            <span>
                {text}
                {Icon(typeOfLink)}
            </span>
        </React.Fragment>
    );
};

const isSignInUrl = (href: string) => {
    const baseUrl = process.env["NEXT_PUBLIC_IDENTITY_BASE_URL"];
    const stargateBaseUrl = process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"];
    const continueUrl = stargateBaseUrl + "/start/authredirect";
    const signInUrl = `${baseUrl}/login?continue=${encodeURIComponent(
        continueUrl
    )}`;

    return href == signInUrl;
};

const isNonSigninOrWacUrl = (href: string) => {
    return !isLocalizableHref(href) && !isSignInUrl(href);
};

export const ConditionalWrapper = (props: PropsWithChildren<LinkProps>) => {
    const children = Children.toArray(props.children);
    if (props.type === "inline" || props.type === "inline-basic") {
        return (
            <React.Fragment>{children.map((child) => child)}</React.Fragment>
        );
    }
    return <div className="link-wrapper">{children.map((child) => child)}</div>;
};

export const Link = ({
    text,
    href,
    type,
    customLinkColor,
    bgColor,
    onClick,
    className,
    doesArrowAnimate = false,
    ariaLabel,
    dataEventProps,
    isStyleOnly = false
}: LinkProps) => {
    const bg = bgColor ? bgColor : "bgWhite";

    let colorDefault = linkColors[bg].default;
    let colorHover = linkColors[bg].hover;
    let colorVisited = linkColors[bg].visited;

    if (customLinkColor) {
        colorDefault = colorHover = colorVisited = customLinkColor;
    }

    const linkStyles = [
        customLinkColor &&
            css({ "&:hover, &:focus": { textShadow: "0 0 1px, 0 0 1px" } }),
        !customLinkColor && css({ "&:hover, &:focus": { textShadow: "" } }),
        type === "basic-underline" &&
            css({ "&:hover": { textDecoration: "underline" } }),
        type === "modal" &&
            css({
                textDecorationLine: "underline",
                textDecorationStyle: "dashed",
                textDecorationThickness: "1px",
                textUnderlineOffset: "3px"
            }),
        (type === "inline" || type === "inline-basic") &&
            css({ textDecoration: "underline" }),
        type !== "modal" &&
            type !== "inline" &&
            type !== "inline-basic" &&
            css({ textDecoration: "none" }),
        doesArrowAnimate &&
            (type === "basic" ||
                type === "basic-underline" ||
                type === "inline-basic") &&
            css({
                "&:hover": {
                    textShadow: "none",
                    svg: {
                        transform: `translateX(${token("space.075")})`
                    }
                }
            }),

        css({
            "alignItems": "center",
            "color": colorDefault,
            "display": "inline-flex",
            "&:visited": {
                color: colorVisited
            },
            "&:hover, &:focus": {
                color: colorHover
            },
            "svg, path": {
                fill: colorDefault
            },
            "&:visited svg path": {
                fill: colorVisited
            },
            "&:hover svg path, &:focus svg path": {
                fill: colorHover
            },
            "svg": {
                transition: "transform 0.25s ease-in-out"
            }
        })
    ];

    return (
        <ConditionalWrapper type={type} text={text} href={href}>
            {!isStyleOnly && (
                <a
                    className={className}
                    href={href}
                    target={
                        type == "external" ||
                        /* redirecting to new tab if the href is not on WAC and not a Signin link */
                        isNonSigninOrWacUrl(href)
                            ? "_blank"
                            : ""
                    }
                    rel={
                        type == "external" || isNonSigninOrWacUrl(href)
                            ? "noreferrer"
                            : ""
                    }
                    css={linkStyles}
                    aria-label={ariaLabel}
                    onClick={onClick}
                    {...dataEventProps}
                >
                    {formatLinkText(text, type)}
                </a>
            )}
            {isStyleOnly && (
                <span className={className} css={linkStyles}>
                    {formatLinkText(text, type)}
                </span>
            )}
        </ConditionalWrapper>
    );
};
